import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { PhotoSelectField, type PhotoSelection } from "@/components/photo-library/PhotoSelectField"
import { inlinePatch } from "@/lib/inline-save"
import type { LibraryPhoto } from "@/components/pages-cms/types"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

export type ConditionCategoryOption = { id: number; label: string }

export type ConditionData = {
  id?: number
  slug: string
  slug_es: string | null
  parent_slug: string | null
  kind: string
  link_path: string | null
  condition_category_id: number | null
  label: string
  nav_label_en: string
  nav_label_es: string
  blurb_en: string | null
  blurb_es: string | null
  meta_title: string
  meta_description: string
  status: string
  position: number
  photo_id?: number | null
  photo_url?: string | null
}

const TEXTAREA_CLASS =
  "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export function ConditionForm({
  condition,
  categories,
  actionUrl,
  method,
  photoLibrary = [],
}: {
  condition: ConditionData
  categories: ConditionCategoryOption[]
  actionUrl: string
  method: "post" | "patch"
  photoLibrary?: LibraryPhoto[]
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const giselaUrl = props.brand_urls.gisela
  const isNew = method === "post"

  // An existing entry saves a field at a time; a new one has nothing to save
  // into yet, so the same boxes collect a draft for one Create.
  const fields = useInlineRecord({
    record: {
      label: condition.label,
      parent_slug: condition.parent_slug ?? "",
      kind: condition.kind || "page",
      link_path: condition.link_path ?? "",
      condition_category_id: condition.condition_category_id
        ? String(condition.condition_category_id)
        : "",
      slug: condition.slug,
      slug_es: condition.slug_es ?? "",
      status: condition.status,
      nav_label_en: condition.nav_label_en,
      nav_label_es: condition.nav_label_es,
      blurb_en: condition.blurb_en ?? "",
      blurb_es: condition.blurb_es ?? "",
      meta_title: condition.meta_title,
      meta_description: condition.meta_description,
      position: String(condition.position),
    },
    url: actionUrl,
    param: "condition",
    enabled: !isNew,
  })
  const v = fields.values

  const [slugEdited, setSlugEdited] = React.useState(!isNew)
  const [navEnEdited, setNavEnEdited] = React.useState(!isNew)
  const [metaTitleEdited, setMetaTitleEdited] = React.useState(!isNew)
  const [processing, setProcessing] = React.useState(false)

  // The card picture, as on the article editor: a library pick sends photo_id,
  // an upload sends the file. On a saved entry it goes up the moment it is
  // chosen — you can see from here whether it is the one you meant.
  const [photo, setPhoto] = React.useState<PhotoSelection>({ kind: "unchanged" })

  function choosePhoto(selection: PhotoSelection) {
    setPhoto(selection)
    if (isNew || selection.kind === "unchanged") return
    if (selection.kind === "library") {
      inlinePatch(actionUrl, { condition: { photo_id: selection.id } })
    } else {
      inlinePatch(actionUrl, { condition: { photo: selection.file } }, { forceFormData: true })
    }
  }

  // On a new page, the name fills in the address, nav label and search title
  // until those are edited by hand.
  function handleLabelChange(value: string) {
    const derived: Record<string, string> = { label: value }
    if (!slugEdited) derived.slug = slugify(value)
    if (!navEnEdited) derived.nav_label_en = value
    if (!metaTitleEdited) derived.meta_title = value ? `${value} | Gisela Ramirez` : ""
    fields.patch(derived)
  }

  // A type of OCD is always a full page in its own right, listed on the OCD
  // hub rather than in the menu — so it has neither a kind nor a category.
  const isSubtype = v.parent_slug !== ""
  const effectiveKind = isSubtype ? "page" : v.kind
  const isPage = effectiveKind === "page"
  const slugChanged = !isNew && v.slug !== condition.slug
  const upgradingToPage = !isNew && isPage && condition.kind !== "page"

  // The three coupled columns: what a subtype or a non-link entry is allowed
  // to hold. They travel with whichever of them was ticked, so the row can
  // never be left saying "link" with no address or "subtype" with a category.
  function coupled() {
    return {
      kind: effectiveKind,
      link_path: effectiveKind === "link" ? v.link_path : "",
      condition_category_id: isSubtype ? "" : v.condition_category_id,
    }
  }

  const path = !v.slug
    ? ""
    : effectiveKind === "link"
      ? v.link_path
      : effectiveKind === "listing"
        ? `/what-i-can-help-with#${v.slug}`
        : isSubtype
          ? `/ocd-therapy/${v.slug}`
          : `/${v.slug}`

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      actionUrl,
      {
        condition: {
          ...v,
          ...coupled(),
          position: Number(v.position),
          ...(photo.kind === "upload" ? { photo: photo.file } : {}),
          ...(photo.kind === "library" ? { photo_id: photo.id } : {}),
        },
      },
      { forceFormData: photo.kind === "upload", onFinish: () => setProcessing(false) },
    )
  }

  function handleDelete() {
    if (
      !window.confirm(
        condition.kind === "page"
          ? `Delete the ${condition.label} page? Its address will stop working and all its content will be removed. This cannot be undone.`
          : `Remove ${condition.label} from the conditions menu? This cannot be undone.`,
      )
    )
      return
    router.delete(actionUrl)
  }

  const labelState = fields.state("label")
  const parentState = fields.state("parent_slug", { with: coupled })
  const categoryState = fields.state("condition_category_id")
  const kindState = fields.state("kind", { with: coupled })
  const linkState = fields.state("link_path")
  const slugState = fields.state("slug")
  const slugEsState = fields.state("slug_es")
  const statusState = fields.state("status")
  const navEnState = fields.state("nav_label_en")
  const navEsState = fields.state("nav_label_es")
  const blurbEnState = fields.state("blurb_en")
  const blurbEsState = fields.state("blurb_es")
  const metaTitleState = fields.state("meta_title")
  const metaDescState = fields.state("meta_description")
  const positionState = fields.state("position", { serialize: (value) => Number(value) })

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <InlineField
        label="Condition name"
        htmlFor="label"
        hint="e.g. Panic Attacks, Social Anxiety, POCD."
        error={errors.label}
        state={labelState}
      >
        <Input
          id="label"
          value={v.label}
          onChange={(e) => handleLabelChange(e.target.value)}
          onKeyDown={labelState.keys}
          required
          placeholder="e.g. Panic Attacks"
          aria-invalid={!!errors.label}
        />
      </InlineField>

      <InlineField label="Page type" htmlFor="parent_slug" error={errors.parent_slug} state={parentState}>
        <Select
          id="parent_slug"
          value={v.parent_slug}
          onChange={(e) => fields.set("parent_slug", e.target.value)}
        >
          <option value="">Its own condition page</option>
          <option value="ocd-therapy">A type of OCD — listed on the OCD Therapy page</option>
        </Select>
      </InlineField>

      {!isSubtype && (
        <>
          <InlineField
            label="Menu group"
            htmlFor="condition_category_id"
            hint="Which group of the “What I help with” menu this appears in."
            error={errors.condition_category_id}
            state={categoryState}
          >
            <Select
              id="condition_category_id"
              value={v.condition_category_id}
              onChange={(e) => fields.set("condition_category_id", e.target.value)}
            >
              <option value="">— Not in the menu —</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-ink-muted">
              <a href="/admin/website/condition-categories">Manage groups →</a>
            </p>
          </InlineField>

          <InlineField
            label="What this entry is"
            htmlFor="kind"
            hint={
              upgradingToPage
                ? "Ticking this creates the page's five content sections ready for drafting — fill in its search title and description first, which a page must have."
                : undefined
            }
            error={errors.kind}
            state={kindState}
          >
            <Select id="kind" value={v.kind} onChange={(e) => fields.set("kind", e.target.value)}>
              <option value="page">Full page — its own address and content</option>
              <option value="listing">
                Listed only — text on the “What I can help with” page, no page of its own
              </option>
              <option value="link">Link to an existing page</option>
            </Select>
          </InlineField>

          {effectiveKind === "link" && (
            <InlineField
              label="Links to"
              htmlFor="link_path"
              hint="A path on Gisela's site, e.g. /ocd-therapy."
              error={errors.link_path}
              state={linkState}
            >
              <Input
                id="link_path"
                value={v.link_path}
                onChange={(e) => fields.set("link_path", e.target.value)}
                onKeyDown={linkState.keys}
                required
                placeholder="/ocd-therapy"
                aria-invalid={!!errors.link_path}
              />
            </InlineField>
          )}
        </>
      )}

      <InlineField
        label="Web address"
        htmlFor="slug"
        hint={
          slugChanged && condition.status === "published"
            ? "Careful: changing the address of a published page breaks links Google has already indexed."
            : undefined
        }
        error={errors.slug}
        state={slugState}
      >
        <Input
          id="slug"
          value={v.slug}
          onChange={(e) => {
            setSlugEdited(true)
            fields.set("slug", e.target.value)
          }}
          onKeyDown={slugState.keys}
          required
          placeholder="e.g. panic-attacks"
          aria-invalid={!!errors.slug}
        />
        {path && (
          <p className="text-xs text-ink-muted">
            {effectiveKind === "page" ? "The page will live at " : "This entry points at "}
            <span className="font-medium text-ink-body">
              {giselaUrl}
              {path}
            </span>
          </p>
        )}
      </InlineField>

      {effectiveKind === "page" && (
        <InlineField
          label="Spanish web address"
          htmlFor="slug_es"
          hint="Optional. With one, the Spanish page lives at its own Spanish address; without, it stays at /es plus the English one."
          error={errors.slug_es}
          state={slugEsState}
        >
          <Input
            id="slug_es"
            value={v.slug_es}
            onChange={(e) => fields.set("slug_es", e.target.value)}
            onKeyDown={slugEsState.keys}
            placeholder="e.g. ataques-de-panico"
            aria-invalid={!!errors.slug_es}
          />
          {v.slug_es && (
            <p className="text-xs text-ink-muted">
              Spanish readers will find it at{" "}
              <span className="font-medium text-ink-body">
                {giselaUrl}
                {isSubtype ? `/terapia-para-el-toc/${v.slug_es}` : `/${v.slug_es}`}
              </span>
            </p>
          )}
        </InlineField>
      )}

      <InlineField
        label="Visibility"
        htmlFor="status"
        hint="Drafts are visible only to you (while logged in) and stay out of Google's sitemap — publish when the copy is ready."
        error={errors.status}
        state={statusState}
      >
        <Select id="status" value={v.status} onChange={(e) => fields.set("status", e.target.value)}>
          <option value="draft">Draft — hidden from visitors</option>
          <option value="published">Published — live on the site</option>
        </Select>
      </InlineField>

      <div className="grid gap-4 sm:grid-cols-2">
        <InlineField
          label="Link label (English)"
          htmlFor="nav_label_en"
          hint="Shown in the site footer and page lists."
          error={errors.nav_label_en}
          state={navEnState}
        >
          <Input
            id="nav_label_en"
            value={v.nav_label_en}
            onChange={(e) => {
              setNavEnEdited(true)
              fields.set("nav_label_en", e.target.value)
            }}
            onKeyDown={navEnState.keys}
            required
            aria-invalid={!!errors.nav_label_en}
          />
        </InlineField>

        <InlineField
          label="Link label (Spanish)"
          htmlFor="nav_label_es"
          error={errors.nav_label_es}
          state={navEsState}
        >
          <Input
            id="nav_label_es"
            value={v.nav_label_es}
            onChange={(e) => fields.set("nav_label_es", e.target.value)}
            onKeyDown={navEsState.keys}
            required
            placeholder="e.g. Terapia para el pánico"
            aria-invalid={!!errors.nav_label_es}
          />
        </InlineField>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <InlineField
          label="One-line description (English)"
          htmlFor="blurb_en"
          hint={
            isSubtype
              ? "Shown on the card in the OCD Therapy page's “Types of OCD” list."
              : "Shown under this name on the “What I can help with” page. Required for anything in the menu."
          }
          error={errors.blurb_en}
          state={blurbEnState}
        >
          <textarea
            id="blurb_en"
            value={v.blurb_en}
            onChange={(e) => fields.set("blurb_en", e.target.value)}
            onKeyDown={blurbEnState.multilineKeys}
            rows={2}
            className={TEXTAREA_CLASS}
            placeholder="The experience in one sentence, in the sufferer's words…"
          />
        </InlineField>

        <InlineField
          label="One-line description (Spanish)"
          htmlFor="blurb_es"
          error={errors.blurb_es}
          state={blurbEsState}
        >
          <textarea
            id="blurb_es"
            value={v.blurb_es}
            onChange={(e) => fields.set("blurb_es", e.target.value)}
            onKeyDown={blurbEsState.multilineKeys}
            rows={2}
            className={TEXTAREA_CLASS}
          />
        </InlineField>
      </div>

      {/* The square beside the name on "What I can help with". Not a tick
          field — a picture is either the one you meant or it isn't, so it
          saves on choosing, the way the article cover does. */}
      <div>
        <label htmlFor="card_photo">Card picture</label>
        <PhotoSelectField
          library={photoLibrary}
          currentUrl={condition.photo_url}
          value={photo}
          onChange={choosePhoto}
          inputId="card_photo"
        />
        <p className="mt-2 text-sm text-ink-muted">
          Shown beside this name on the &ldquo;What I can help with&rdquo; page. Leave it empty and the
          cover of the first article filed under this condition is used instead; with neither, the
          square is a plain tint. Uploads join the photo library.
        </p>
      </div>

      {/* Only a full page is indexed, so only a full page needs metadata. */}
      {isPage && (
        <>
          <InlineField
            label="Search title"
            htmlFor="meta_title"
            hint={`Shown as the page's title in Google. Keep it under 60 characters (currently ${v.meta_title.length}).`}
            error={errors.meta_title}
            state={metaTitleState}
          >
            <Input
              id="meta_title"
              value={v.meta_title}
              onChange={(e) => {
                setMetaTitleEdited(true)
                fields.set("meta_title", e.target.value)
              }}
              onKeyDown={metaTitleState.keys}
              required
              placeholder="e.g. Panic Attack Therapy | CBT Online UK | Gisela Ramirez"
              aria-invalid={!!errors.meta_title}
            />
          </InlineField>

          <InlineField
            label="Search description"
            htmlFor="meta_description"
            hint={`The snippet under the title in Google — 120–160 characters works best (currently ${v.meta_description.length}).`}
            error={errors.meta_description}
            state={metaDescState}
          >
            <textarea
              id="meta_description"
              value={v.meta_description}
              onChange={(e) => fields.set("meta_description", e.target.value)}
              onKeyDown={metaDescState.multilineKeys}
              required
              rows={3}
              className={TEXTAREA_CLASS}
              placeholder="What the page offers and for whom, written for someone scanning search results…"
              aria-invalid={!!errors.meta_description}
            />
          </InlineField>
        </>
      )}

      <InlineField
        label="Display order"
        htmlFor="position"
        hint="Lower numbers appear first within this menu group."
        error={errors.position}
        state={positionState}
        className={cn("max-w-40")}
      >
        <Input
          id="position"
          type="number"
          value={v.position}
          onChange={(e) => fields.set("position", e.target.value)}
          onKeyDown={positionState.keys}
          min={0}
        />
      </InlineField>

      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
        {isNew ? (
          <>
            <Button type="submit" disabled={processing}>
              {isPage ? "Create page" : "Create entry"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => window.history.back()}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-ink-muted">
              Each change is kept when you tick it — there's nothing else to save.
            </p>
            <Button type="button" variant="ghost" className="ml-auto text-danger-display" onClick={handleDelete}>
              {condition.kind === "page" ? "Delete page" : "Remove entry"}
            </Button>
          </>
        )}
      </div>
    </form>
  )
}
